import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MidaApiService } from '../mida-api.service';
import { catchError, map, mergeMap, of } from 'rxjs';
import { AppActions } from './app.actions';

@Injectable()
export class AppEffects {
  private readonly midaAPIService = inject(MidaApiService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  getCollectionChartData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getDashboardData),
      mergeMap(() =>
        this.midaAPIService.getDashboardData().pipe(
          map((dashboardData) => {
            this.store.dispatch(AppActions.getCollectionChartDataSuccess({ collectionChart: dashboardData.collection }));
            this.store.dispatch(AppActions.getPerformanceOverviewDataSuccess({ performanceOverview: dashboardData.performance }));
            return AppActions.getDashboardDataSuccess();
          }),
          catchError(() => of(AppActions.getDashboardDataFailure()))
        )
      )
    )
  );

  getOrganisationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getOrganisationList),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getOrganisationList(orgKey).pipe(
          map((organisationList) => AppActions.getOrganisationListSuccess({ organisations: organisationList })),
          catchError(() => of(AppActions.getOrganisationListFailure()))
        )
      )
    )
  );

  getRepaymentInsights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getRepaymentInsights),
      mergeMap(({ startDate, endDate }) =>
        this.midaAPIService.getRepaymentInsights(startDate, endDate).pipe(
          map((repaymentInsights) => AppActions.getRepaymentInsightsSuccess({ repaymentInsights })),
          catchError(() => of(AppActions.getRepaymentInsightsFailure()))
        )
      )
    )
  );

  getOrganisationFullList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getOrganisationFullList),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getOrganisationFullList(orgKey).pipe(
          map((organisationList) => AppActions.getOrganisationFullListSuccess({ organisationsFullList: organisationList })),
          catchError(() => of(AppActions.getOrganisationFullListFailure()))
        )
      )
    )
  );

  getMandates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getMandates),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getAllmandate(orgKey).pipe(
          map((mandates) => AppActions.getMandatesSuccess({ mandates })),
          catchError(() => of(AppActions.getMandatesFailure()))
        )
      )
    )
  );

  getRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getRoles),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getRoles(orgKey).pipe(
          map((roles) => AppActions.getRolesSuccess({ roles })),
          catchError(() => of(AppActions.getRolesFailure()))
        )
      )
    )
  );

  // getSettings$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(AppActions.getSettings),
  //     mergeMap(() =>
  //       this.midaAPIService.getOrganisationSettings().pipe(
  //         map((settings) => AppActions.getSettingsSuccess({ settings })),
  //         catchError(() => of(AppActions.getSettingsFailure()))
  //       )
  //     )
  //   )
  // );

  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getUsers),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getUsers(orgKey).pipe(
          map((users) => AppActions.getUsersSuccess({ users })),
          catchError(() => of(AppActions.getUsersFailure()))
        )
      )
    )
  );

  bulkCreateUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.bulkCreateUsers),
      mergeMap(({ payload }) =>
        this.midaAPIService.bulkRegistration(payload).pipe(
          mergeMap(() => [AppActions.bulkCreateUsersSuccess(), AppActions.getUsers({ orgKey: payload.orgKey })]),
          catchError(() => of(AppActions.bulkCreateUsersFailure()))
        )
      )
    )
  );

  getLoansCollectedThisMonth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getLoansCollectedThisMonth),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getLoansCollectedThisMonth(orgKey).pipe(
          map((loansCollectedThisMonth) => AppActions.getLoansCollectedThisMonthSuccess({ loansCollectedThisMonth })),
          catchError(() => of(AppActions.getLoansCollectedThisMonthFailure()))
        )
      )
    )
  );

  getLoansDueThisMonth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getLoansDueThisMonth),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getLoansDueThisMonth(orgKey).pipe(
          map((loansDueThisMonth) => AppActions.getLoansDueThisMonthSuccess({ loansDueThisMonth })),
          catchError(() => of(AppActions.getLoansDueThisMonthFailure()))
        )
      )
    )
  );

  getOverdueLoans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getOverdueLoans),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getOverdueLoans(orgKey).pipe(
          map((overdueLoans) => AppActions.getOverdueLoansSuccess({ overdueLoans })),
          catchError(() => of(AppActions.getOverdueLoansFailure()))
        )
      )
    )
  );

  getPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getPermission),
      mergeMap(() =>
        this.midaAPIService.getPermission().pipe(
          map((permissions) => AppActions.getPermissionSuccess({ permissions })),
          catchError(() => of(AppActions.getPermissionFailure()))
        )
      )
    )
  );

  getBanks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getBanks),
      mergeMap(() =>
        this.midaAPIService.getBanks().pipe(
          map((res) => AppActions.getBanksSuccess({ banks: res.data })),
          catchError(() => of(AppActions.getBanksFailure()))
        )
      )
    )
  );

  getBilling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getBilling),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getBilling(orgKey).pipe(
          map((billing) => AppActions.getBillingSuccess({ billing: billing.items })),
          catchError(() => of(AppActions.getBillingFailure()))
        )
      )
    )
  );

  getTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getTransactionList),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getTransactions(orgKey).pipe(
          map((transactions) => AppActions.getTransactionListSuccess({ transactions })),
          catchError(() => of(AppActions.getTransactionListFailure()))
        )
      )
    )
  );

  getCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getCustomerList),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getCustomers(orgKey).pipe(
          map((customers) => AppActions.getCustomerListSuccess({ customers })),
          catchError(() => of(AppActions.getCustomerListFailure()))
        )
      )
    )
  );

  getLoansSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getLoanSummary),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getLoanSummary(orgKey).pipe(
          map((loanSummary) => AppActions.getLoanSummarySuccess({ loanSummary })),
          catchError(() => of(AppActions.getLoanSummaryFailure()))
        )
      )
    )
  );

  getLoans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getLoanList),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getAllLoans(orgKey).pipe(
          map((loans) => AppActions.getLoanListSuccess({ loans })),
          catchError(() => of(AppActions.getLoanListFailure()))
        )
      )
    )
  );

  getSettlement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getSettlement),
      mergeMap(({ orgKey }) =>
        this.midaAPIService.getSettlement(orgKey).pipe(
          map((settlement) => AppActions.getSettlementSuccess({ settlement: settlement })),
          catchError(() => of(AppActions.getSettlementFailure()))
        )
      )
    )
  );

  getGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getGroups),
      mergeMap(() =>
        this.midaAPIService.getGroups().pipe(
          map((groups) => AppActions.getGroupsSuccess({ groups })),
          catchError(() => of(AppActions.getGroupsFailure()))
        )
      )
    )
  );
}
